import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/projects`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      setProjects(data.projects || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { projects, categories, loading, refresh };
}

export function useAdminProjects(token) {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      setProjects(data.projects || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to load admin projects:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const createProject = useCallback(async (body) => {
    const res = await fetch(`${API}/api/admin/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Fehler'); }
    const data = await res.json();
    await refresh();
    return data.project;
  }, [token, refresh]);

  const updateProject = useCallback(async (id, body) => {
    const res = await fetch(`${API}/api/admin/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Fehler'); }
    const data = await res.json();
    await refresh();
    return data.project;
  }, [token, refresh]);

  const deleteProject = useCallback(async (id) => {
    const res = await fetch(`${API}/api/admin/projects/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Fehler'); }
    await refresh();
  }, [token, refresh]);

  const reorderProjects = useCallback(async (order) => {
    const res = await fetch(`${API}/api/admin/projects/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ order }),
    });
    if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Fehler'); }
    await refresh();
  }, [token, refresh]);

  return { projects, categories, loading, refresh, createProject, updateProject, deleteProject, reorderProjects };
}
